import {
  createCustomerRequestSchema,
  type CreateCustomerRequest,
  type Customer,
} from "@stb/api-contract"
import { AlertTriangleIcon, Loader2 } from "lucide-react"
import { useId, useRef, useState } from "react"
import { flushSync } from "react-dom"

import { Alert, AlertDescription } from "@/ui/alert"
import { Button } from "@/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog"
import { Input } from "@/ui/input"
import { Label } from "@/ui/label"

import { describeRpcError } from "../../rpc-error"
import type { RpcResult } from "../../rpc/context"

type FieldKey = "name" | "email" | "abn" | "address" | "payment_terms_days" | "notes"

const MESSAGES: Record<FieldKey, string> = {
  name: "Enter the customer's name.",
  email: "Enter a valid email address.",
  abn: "Enter a valid 11 digit ABN.",
  address: "The address is too long.",
  payment_terms_days: "Payment terms are a whole number of days, 0 to 365.",
  notes: "The notes are too long.",
}

const orNull = (text: string) => (text.trim() === "" ? null : text.trim())

export interface CustomerFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Edit this customer; omit to create. */
  customer?: Customer
  onSubmit: (request: CreateCustomerRequest) => Promise<RpcResult<Customer>>
  onDone: (customer: Customer) => void
}

/** Create or edit a customer; ABN and email are checked inline before anything is sent (R2.1). */
export function CustomerFormDialog({
  open,
  onOpenChange,
  customer,
  onSubmit,
  onDone,
}: CustomerFormDialogProps) {
  const ids = useId()
  const [values, setValues] = useState({
    name: customer?.name ?? "",
    email: customer?.email ?? "",
    abn: customer?.abn ?? "",
    address: customer?.address ?? "",
    payment_terms_days:
      customer?.payment_terms_days === null || customer === undefined
        ? ""
        : String(customer.payment_terms_days),
    notes: customer?.notes ?? "",
  })
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({})
  const [serverError, setServerError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const input = (key: FieldKey, label: string, extra: React.ComponentProps<typeof Input> = {}) => {
    const id = `${ids}-${key}`
    return (
      <div className="space-y-2">
        <Label htmlFor={id}>{label}</Label>
        <Input
          id={id}
          value={values[key]}
          onChange={(event) => setValues((current) => ({ ...current, [key]: event.target.value }))}
          aria-invalid={errors[key] ? true : undefined}
          aria-describedby={errors[key] ? `${id}-error` : undefined}
          {...extra}
        />
        {errors[key] && (
          <p id={`${id}-error`} role="alert" className="text-destructive text-sm">
            {errors[key]}
          </p>
        )}
      </div>
    )
  }

  async function submit(event: React.SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    setServerError(null)
    const terms = values.payment_terms_days.trim()
    const request: CreateCustomerRequest = {
      name: values.name.trim(),
      email: orNull(values.email),
      abn: orNull(values.abn),
      address: orNull(values.address),
      payment_terms_days: terms === "" ? null : /^\d+$/.test(terms) ? Number(terms) : Number.NaN,
      notes: orNull(values.notes),
    }
    const found: Partial<Record<FieldKey, string>> = {}
    const parsed = createCustomerRequestSchema.safeParse(request)
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as FieldKey
        if (key in MESSAGES) found[key] ??= MESSAGES[key]
      }
    }
    if (Object.keys(found).length > 0) {
      flushSync(() => setErrors(found))
      formRef.current?.querySelector<HTMLElement>("[aria-invalid=true]")?.focus()
      return
    }
    setErrors({})
    setPending(true)
    try {
      const result = await onSubmit(request)
      if (result.ok) onDone(result.data)
      else setServerError(describeRpcError(result.error).message)
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{customer ? "Edit customer" : "New customer"}</DialogTitle>
          <DialogDescription>
            A name is enough. An ABN is only required on a tax invoice of A$1,000 or more when there
            is no name.
          </DialogDescription>
        </DialogHeader>
        <form ref={formRef} onSubmit={submit} noValidate className="space-y-4">
          {serverError && (
            <Alert variant="destructive">
              <AlertTriangleIcon aria-hidden="true" />
              <AlertDescription>{serverError}</AlertDescription>
            </Alert>
          )}
          {input("name", "Name", { autoComplete: "off" })}
          <div className="grid gap-4 sm:grid-cols-2">
            {input("abn", "ABN (optional)", { inputMode: "numeric" })}
            {input("email", "Email (optional)", { type: "email" })}
          </div>
          {input("address", "Address (optional)")}
          {input("payment_terms_days", "Payment terms in days (optional)", {
            inputMode: "numeric",
          })}
          {input("notes", "Notes (optional, only you see these)")}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
              Save customer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
